import { RejectedBannerIcon } from '@/assets/icons/info-icons/rejectedBanner';
import { Banner } from '@/components/basicComponents/banner';
import ColorPicker from '@/components/basicComponents/colorPicker';
import { FormFooter } from '@/components/basicComponents/formFooter';
import ModalLoader from '@/components/basicComponents/modal/modalLoader';
import ModalWrappedContent from '@/components/basicComponents/modal/modalWrappedContent';
import Input from '@/components/basicComponents/input';
import Header from '@/components/header';
import { GoBackButton } from '@/components/header/goBackButton';
import { PageTitle } from '@/components/header/pageTitle';
import { useErrorMessages } from '@/hooks/i18n';
import { useResponsive } from '@/hooks/use-responsive';
import { useClickOutside } from '@/hooks/useClickOutside';
import { useCustomizationsData } from '@/hooks/useCustomizationsData';
import { MainWrap } from '@/styles/common';
import { themeVar } from '@/styles/theme';
import axios from 'axios';
import { useEffect, useRef, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import styled, { useTheme } from 'styled-components';
import { SettingsContainer, SettingsSection } from '../..';

const colorInputFields = [
  [
    { label: 'Important Button', name: 'action.primary' },
    { label: 'Default Button', name: 'action.default' },
    { label: 'Button Hover State', name: 'action.hover' },
    { label: 'Selected Item', name: 'action.active' }
  ],
  [
    { label: 'Text Link', name: 'font.action' },
    { label: 'Nav Background', name: 'header.base' },
    { label: 'Action Nav Item', name: 'menu.active' },
    { label: 'Nav Labels', name: 'header.action' }
  ]
];

const addHashToColors = (colorObj: { [key: string]: string }): { [key: string]: string } => {
  const newColorObj: any = {};
  for (const key in colorObj) {
    if (colorObj.hasOwnProperty(key)) {
      newColorObj[key] = `#${colorObj[key]}`;
    }
  }
  return newColorObj;
};

interface CatalogBannerTextProps {
  colorsData: any[];
  handleClose: () => void;
}

export const EditColors = ({ colorsData, handleClose }: CatalogBannerTextProps) => {
  const theme = useTheme();
  const { isMobile } = useResponsive();
  const { default: defaultErrorMessage } = useErrorMessages();
  const { updateCustomizationsDataByKey } = useCustomizationsData();
  const { control, setValue, handleSubmit } = useForm();

  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [colorPicker, setColorPicker] = useState({ name: '', isOpen: false });

  const colorPickerRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!colorsData.length) return;

    colorsData?.forEach((element) => {
      setValue(element.themeVariableName, element.value.replace('#', ''));
    });
  }, [colorsData]);

  const handleColorPicker = (name?: string) => {
    if (colorPicker.isOpen && !name) {
      setColorPicker({ name: '', isOpen: false });
      return;
    }
    setColorPicker({ name, isOpen: true });
  };

  const onSubmit = async (data: any) => {
    setErrorMessage(null);
    setLoading(true);

    try {
      const { action, font, header, menu } = data;
      const updatedTheme = {
        ...theme,
        action: { ...theme.action, ...addHashToColors(action) },
        font: { ...theme.font, ...addHashToColors(font) },
        header: { ...theme.header, ...addHashToColors(header), layer2: `#${menu.active}` },
        menu: { ...theme.menu, ...addHashToColors(menu), tab: `#${header.base}` },
        footer: { ...theme.footer, base: `#${header.base}` },
        loginBg: `#${header.base}`
      };

      const { data: responseData } = await axios.post(
        `${process.env.REACT_APP_BACKEND_BASE_URL}/system/update-customization`,
        {
          key: 'theme',
          value: JSON.stringify(updatedTheme)
        },
        {
          headers: { authorization: `Bearer ${localStorage.getItem('token')}` }
        }
      );

      if (responseData?.value) {
        updateCustomizationsDataByKey('theme', JSON.parse(responseData.value));
      }
      handleClose();
    } catch (error) {
      setErrorMessage(defaultErrorMessage);
    } finally {
      setLoading(false);
    }
  };

  useClickOutside<HTMLDivElement>(colorPickerRef, () => handleColorPicker());

  return (
    <>
      {loading && (
        <ModalWrappedContent
          isOpen={loading}
          title="Saving"
          showCancelButton={false}
          showCloseIcon={false}
          isUseOutsideClick={false}
          onClose={() => {}}
        >
          <ModalLoader />
        </ModalWrappedContent>
      )}
      <MainWrap>
        <Header modalControl={<GoBackButton handleClose={handleClose} backToTitle="Firm Settings" />} />
        <PageTitle title="Colors" />
      </MainWrap>
      <MainWrap>
        {errorMessage && (
          <Banner
            icon={<RejectedBannerIcon fill={themeVar('context.light')} />}
            title="Error"
            description={errorMessage}
            bgColor={themeVar('context.error')}
          />
        )}
        <SettingsContainer>
          <SettingsSection isMobile={isMobile}>
            <ColorRowsWrapper>
              {colorInputFields.map((row, index) => (
                <FourColumnWrapper key={index} isMobile={isMobile}>
                  {row.map((field) => (
                    <Controller
                      key={field.name}
                      name={field.name}
                      control={control}
                      rules={{
                        required: {
                          value: true,
                          message: 'Field Required'
                        },
                        pattern: {
                          value: /^([0-9A-F]{3}){1,2}([0-9A-F]{2})?$/i,
                          message: 'Invalid HEX color'
                        }
                      }}
                      render={({ field: { onChange, value }, fieldState: { error } }) => (
                        <InputWrapper>
                          <Input value={value || ''} onChange={onChange} label={field.label} errorText={error?.message} isRequiredField />
                          <ColorPreview bgColor={`#${value}`} onClick={() => handleColorPicker(field.name)} />
                          {colorPicker.isOpen && colorPicker.name === field.name && (
                            <Popover ref={colorPickerRef}>
                              <PopoverContent>
                                <ColorPicker color={value} onChange={onChange} />
                              </PopoverContent>
                            </Popover>
                          )}
                        </InputWrapper>
                      )}
                    />
                  ))}
                </FourColumnWrapper>
              ))}
            </ColorRowsWrapper>
          </SettingsSection>
          <FormFooter onCancel={handleClose} onSave={handleSubmit(onSubmit)} disableSaveButton={loading} showRequiredFieldsWarning />
        </SettingsContainer>
      </MainWrap>
    </>
  );
};

const ColorRowsWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 25px;
`;

const FourColumnWrapper = styled.div<{ isMobile: boolean }>`
  display: grid;
  grid-template-columns: ${({ isMobile }) => (isMobile ? '1fr' : 'repeat(4, 1fr)')};
  gap: 10px;
`;

const InputWrapper = styled.div`
  position: relative;
`;

const ColorPreview = styled.div<{ bgColor: string }>`
  position: absolute;
  top: 49px;
  right: 12px;
  width: 24px;
  height: 24px;
  cursor: pointer;
  border-radius: 20px;
  border: 1px solid ${themeVar('border.base')};
  background-color: ${({ bgColor }) => bgColor};
`;

const Popover = styled.div`
  position: absolute;
  z-index: 2;
  top: 100px;
  left: 0;
`;

const PopoverContent = styled.div`
  position: relative;
  border-radius: 10px;
  box-shadow: 0 0 10px rgba(0, 0, 0, 0.2);
`;
